import { AppContext } from "@/App.context";
import config from "@/config";
import {
  API_LOADING_ERROR_CUSTOM_WIDGET,
  API_NO_DATA_FOUND_ERROR_CUSTOM_WIDGET,
  API_NOT_FOUND_ERROR_CUSTOM_WIDGET,
  ERROR_STATUS_CODES,
} from "@/lib/errors/error-constants";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";

/**
 * @typedef GetAllStatisticsFilters
 * @property {keyof typeof AWS_TAGS | keyof typeof AZURE_TAGS} filterOn
 * @property {'include' | 'exclude'} filterType
 * @property {string[]} values
 */

/**
 * @typedef GetAllStatistics
 * @property {GetTotalStatistics} idle_resource_total
 * @property {GetIdleStatistics} idle_resources
 * @property {number} savings_till_date
 * @property {number} savings_available
 */
/**
 * @typedef GetTotalStatistics
 * @property {number} idle_resource_count
 * @property {number} unblended_cost
 */

/**
 * @typedef GetIdleStatistics
 * @property {GetDiskStatistics=} disk
 * @property {GetVmStatistics=} vm
 * @property {GetIpStatistics=} ip
 * @property {GetLoadStatistics=} lb
 */

/**
 * @typedef GetDiskStatistics
 * @property {number} resource_type
 * @property {number} count
 * @property {number} cost
 */

/**
 * @typedef GetVmStatistics
 * @property {number} resource_type
 * @property {number} count
 * @property {number} cost
 */
/**
 * @typedef GetLoadStatistics
 * @property {number} resource_type
 * @property {number} count
 * @property {number} cost
 */

/**
 * @typedef GetIpStatistics
 * @property {number} resource_type
 * @property {number} count
 * @property {number} cost
 */

/**
 * @typedef GetIdleFilters
 * @property {string[]} accounts
 * @property {string[]} rg
 * @property {string[]} regions
 * @property {string[]} tags
 */

// /**
//  * @typedef GetResultStatistics
//  * @property {number} saving_available  - Email address
//  */

/**
 * @typedef GetAllStatisticsRes
 * @property {GetAllStatistics[]} statistics
 *  @property {GetIdleFilters} filters
 */
/**
 * @typedef GetStatisticsReq
 * @property {'aws' | 'gcp' | 'azure' | 'all-cloud' } service
 * @property { 'false' | 'true' } untagged
 */

export const getStatistics = (token) => {
  /**
   * @param {GetStatisticsReq} req
   * @param {AbortSignal=} signal
   * @returns {Promise<GetAllStatistics>}
   */
  return async ({ service, filters, untagged }, signal) => {
    const body = {
      accounts: filters?.accounts || [],
      regions: filters?.regions || [],
      tags: filters?.tags || [],
    };

    if (service === "azure") {
      body.rg = filters?.rg || [];
    }
    const url = new URL(
      `${config.apiHost}/v1/${service}/idle-resources/total-stats`
    );

    if (untagged) {
      url.searchParams.append("untagged", "true");
    }
    const owners = await fetch(url.toString(), {
      method: service === "all-cloud" ? "GET" : "POST",
      body: service !== "all-cloud" ? JSON.stringify(body) : undefined,
      signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    });
    if (!owners.ok) {
      if (ERROR_STATUS_CODES.includes(owners.status)) {
        throw new Error(API_LOADING_ERROR_CUSTOM_WIDGET);
      }
      if (owners.status === 404) {
        throw new Error(API_NOT_FOUND_ERROR_CUSTOM_WIDGET);
      }
      throw new Error((await owners.json()).message);
    }
    const data = await owners.json();

    if (
      (!data?.idle_resource_total?.idle_resource_count ||
        data?.idle_resource_total?.idle_resource_count === 0) &&
      (!data?.idle_resource_total?.unblended_cost ||
        data?.idle_resource_total?.unblended_cost === 0) &&
      (!data?.idle_resources?.ip?.count ||
        data?.idle_resources?.ip?.count === 0) &&
      (!data?.idle_resources?.vm?.count ||
        data?.idle_resources?.vm?.count === 0) &&
      (!data?.idle_resources?.disk?.count ||
        data?.idle_resources?.disk?.count === 0) &&
      (!data?.idle_resources?.lb?.count ||
        data?.idle_resources?.lb?.count === 0) &&
      (!data?.saving_available?.saving_available ||
        data?.saving_available?.saving_available === 0) &&
      (!data?.savings_till_date || data?.savings_till_date === 0)
    ) {
      throw new Error(API_NO_DATA_FOUND_ERROR_CUSTOM_WIDGET);
    }

    return data;
  };
};

/**
 * @param {GetStatisticsReq} req
 * @param {any[]=} deps
 */
export const useStatistics = (req, deps = []) => {
  const appContext = useContext(AppContext);
  const query = useQuery({
    queryKey: ["getStatistics", appContext.idToken, req, ...deps],
    queryFn: ({ signal }) => getStatistics(appContext.idToken)(req, signal),
  });
  return query;
};
